import { mapLimited } from "./map-limited.js";
import { type PartState, PartStates } from "./part-state.js";
import { SheetShards } from "./sheet-shards.js";
import {
  concurrency,
  counted,
  pageFetch,
  type SheetKind,
  sheetTitle,
  type SheetToolProperties,
  startSheetTool,
} from "./sheet-tool.js";
import {
  vocabularyChoice,
  vocabularyFileOf,
  type VocabularyKeys,
  vocabularyKeys,
  vocabularyNeeds,
  type VocabularyPart,
  type VocabularyShard,
  vocabularyShard,
  VocabularySheet,
} from "./vocabulary-sheet.js";
import { GlyphLibrary } from "./worksheet-drawing.js";
import { sheetOrder } from "./worksheet-seed.js";

/**
 * The vocabulary worksheet generator: the 词汇 sheet as a kind of sheet
 * `sheet-tool.ts` draws.
 */

/** The parts, in the order they print, and whether each prints by default. */
export const vocabularyStates = new PartStates<VocabularyPart>([
  ["zuci", true],
  ["collocations", true],
  ["opposites", true],
  ["key", true],
]);

/** The most words one sheet looks at. */
export const vocabularyWordLimit = 300;

/** The vocabulary sheet, drawn from the items its words need. */
export function vocabularySheetKind(
  data: SheetShards<VocabularyKeys, VocabularyShard>,
): SheetKind<PartState<VocabularyPart>> {
  return {
    read: (hash) => vocabularyStates.read(hash),
    fragment: (state) => vocabularyStates.fragment(state),
    nothingAsked: (state) =>
      state.zuci || state.collocations || state.opposites
        ? undefined
        : "Tick at least one exercise under Include.",
    title: (state, list) =>
      sheetTitle(state, list, {
        typed: "Vocabulary practice",
        saved: "Vocabulary from my saved words",
      }),

    draw: async ({ state, words, entries, labels }) => {
      const given = words.slice(0, vocabularyWordLimit);
      const keys = await data.keys();
      const items =
        keys === undefined
          ? undefined
          : await data.items(vocabularyNeeds(keys, given));
      if (items === undefined) {
        return "Could not load the worksheet data. Try again in a moment.";
      }
      const choice = vocabularyChoice(vocabularyFileOf(items), given);
      const asked =
        (state.zuci
          ? choice.zuci.reduce((sum, item) => sum + item.slots.length, 0)
          : 0) +
        (state.collocations ? choice.collocations.length : 0) +
        (state.opposites ? choice.opposites.length : 0);
      if (asked === 0) {
        return "No questions are available for these words and exercises. Try more words or tick another exercise.";
      }

      const library = new GlyphLibrary({ document });
      if (state.zuci) {
        const characters = choice.zuci.map((item) => item.character);
        const outlines = await mapLimited(
          characters,
          concurrency,
          (character) => entries.strokes(character),
        );
        for (const [at, character] of characters.entries()) {
          library.hold(character, outlines[at]);
        }
      }
      const pages = new VocabularySheet({ library }).pages(
        state,
        {
          ...choice,
          zuci: sheetOrder(choice.zuci, state),
          collocations: sheetOrder(choice.collocations, state),
          opposites: sheetOrder(choice.opposites, state),
        },
        labels,
      );
      const left =
        choice.unasked.length === 0
          ? ""
          : ` No questions available for ${choice.unasked.slice(0, 12).join(" ")}${choice.unasked.length > 12 ? " and others" : ""}.`;

      return {
        nodes: [library.element, ...pages],
        status: `${counted(asked, "question", "questions")} on ${counted(pages.length, "page", "pages")}.${left}`,
      };
    },
  };
}

/** Start the vocabulary worksheet generator on its container. */
export function startVocabularyWorksheetTool(
  container: HTMLElement,
  properties: SheetToolProperties = pageFetch,
): void {
  const data = new SheetShards({
    fetch: properties.fetch,
    address: container.dataset["cbData"] ?? "keys.json",
    readKeys: vocabularyKeys,
    readItem: vocabularyShard,
    limit: concurrency,
  });
  startSheetTool(container, vocabularySheetKind(data), properties);
}
